import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Shield, Truck, Headphones, Award, Users, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getBanners, getCategories, getProducts, getPageContent } from '../services/firebaseService';

const PRODUCT_PLACEHOLDER = "https://pub-2603f7a1a1604332b276f2ff8fff86f5.r2.dev/polymax/placeholder.png";

const iconMap = {
  shield: Shield,
  truck: Truck,
  headphones: Headphones,
  award: Award,
  users: Users,
  heart: Heart,
};

const HomePage = () => {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [aboutContent, setAboutContent] = useState(null);
  const [whyFeatures, setWhyFeatures] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBanner((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const fetchData = async () => {
    try {
      const [bannersData, categoriesData, productsData, aboutData, whyData] = await Promise.all([
        getBanners(true),
        getCategories(),
        getProducts(),
        getPageContent('about'),
        getPageContent('why_choose_us'),
      ]);
      setBanners(bannersData);
      setCategories(categoriesData);
      setProducts(productsData.slice(0, 8));
      setAboutContent(aboutData?.content || {});
      setWhyFeatures(whyData?.content?.features || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1e3a5f] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Banner Section */}
      <section className="relative h-[600px] md:h-[700px] overflow-hidden" data-testid="hero-section">
        {banners.length > 0 ? (
          <>
            {banners.map((banner, index) => (
              <div
                key={banner.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentBanner ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${banner.image_url})` }}
                />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-2xl animate-fade-in-up">
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                        {banner.title}
                      </h1>
                      <p className="text-xl text-white/90 mb-8">
                        {banner.subtitle}
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <Link to="/products">
                          <Button size="lg" className="bg-white text-[#1e3a5f] hover:bg-white/90 font-semibold">
                            Məhsullara Bax
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                        </Link>
                        <Link to="/contact">
                          <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                            Əlaqə
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {banners.length > 1 && (
              <>
                <button
                  onClick={prevBanner}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 hover:bg-white/30 text-white transition-all"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextBanner}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 hover:bg-white/30 text-white transition-all"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentBanner(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === currentBanner ? 'bg-white w-8' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="absolute inset-0 bg-[#1e3a5f] flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl font-bold mb-4">PolyMax Medicine-ə Xoş Gəlmisiniz</h1>
              <p className="text-xl">Keyfiyyətli dərmanlar, sağlam həyat</p>
            </div>
          </div>
        )}
      </section>

      {/* About Section */}
      <section className="py-20 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-6">
                Haqqımızda
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {aboutContent?.description || 'PolyMax Medicine, Azərbaycanda əczaçılıq sahəsində lider şirkətlərdən biridir. Biz 2010-cu ildən bəri fəaliyyət göstəririk və müştərilərimizə yüksək keyfiyyətli tibbi məhsullar təqdim edirik.'}
              </p>
              <Link to="/about">
                <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
                  Ətraflı Oxu
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative animate-fade-in-up">
              {aboutContent?.image_url && (
                <img
                  src={aboutContent.image_url}
                  alt="PolyMax Medicine"
                  className="rounded-2xl shadow-2xl w-full"
                />
              )}
              <div className="absolute -bottom-6 -right-6 bg-[#1e3a5f] text-white p-8 rounded-2xl shadow-xl">
                <p className="text-4xl font-bold">15+</p>
                <p className="text-sm text-white/80">İllik Təcrübə</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 bg-[#f8fafc]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-2">
                Məhsullarımız
              </h2>
              <p className="text-slate-600">
                Ən populyar dərmanlarımız
              </p>
            </div>
            <Link to="/products">
              <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
                Hamısına Bax
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {products.map((product) => (
              <Link key={product.id} to={`/products/${product.id}`} className="group">
                <Card className="card-hover overflow-hidden">
                  <div className="aspect-square bg-slate-100 relative overflow-hidden">
                    <img
                      src={product.image_url || PRODUCT_PLACEHOLDER}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-[#1e3a5f] mb-1 line-clamp-1 group-hover:text-[#3b82f6] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                      {product.description || 'Keyfiyyətli dərman'}
                    </p>
                    {product.show_price && product.price ? (
                      <p className="font-bold text-[#3b82f6]">{product.price} AZN</p>
                    ) : null}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">
              Kateqoriyalar
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Müxtəlif kateqoriyalarda keyfiyyətli dərmanlar
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {categories.map((category) => (
              <Link 
                key={category.id} 
                to={`/products?category=${category.id}`}
                className="group"
              >
                <Card className="card-hover h-full border-slate-100">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#1e3a5f]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#1e3a5f] transition-colors">
                      <Shield className="h-8 w-8 text-[#1e3a5f] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-[#1e3a5f] group-hover:text-[#3b82f6] transition-colors">
                      {category.name}
                    </h3>
                    {category.description && (
                      <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                        {category.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      {whyFeatures.length > 0 && (
        <section className="py-20 bg-white" data-testid="why-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-4">
                Niyə Bizi Seçməlisiniz?
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                PolyMax Medicine ilə fərqi hiss edin
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
              {whyFeatures.map((feature, index) => {
                const IconComponent = iconMap[feature.icon] || Shield;
                return (
                  <Card key={index} className="card-hover border-slate-100">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 bg-[#1e3a5f]/10 rounded-2xl flex items-center justify-center mb-4">
                        <IconComponent className="h-7 w-7 text-[#1e3a5f]" />
                      </div>
                      <h3 className="font-semibold text-lg text-[#1e3a5f] mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 text-sm">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Bizimlə Əlaqə Saxlayın
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Suallarınız var? Peşəkar komandamız sizə kömək etməyə hazırdır.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#1e3a5f] hover:bg-white/90 font-semibold">
                Əlaqə Səhifəsi
              </Button>
            </Link>
            <a href="https://wa.me/994506555501" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#25d366] hover:bg-[#25d366]/90 text-white font-semibold">
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
